/**
 * Photos Module Types
 * 
 * Type definitions for the family photo gallery feature.
 */

import { Profile } from "@/types";

/**
 * Activity type for photo context
 */
export type PhotoActivityType = 
  | "general" 
  | "challenge" 
  | "speurtocht" 
  | "agenda_event";

/**
 * Photo file from database (extends files table)
 */
export interface Photo {
  id: string;
  weekend_id: string | null;
  uploaded_by: string;
  team_id: string | null;
  game_id: string | null;
  
  // File info
  name: string;
  original_name: string;
  file_url: string;
  thumbnail_url: string | null;
  file_type: string;
  file_size: number;
  width: number | null;
  height: number | null;
  
  // Context
  category: string;
  activity_type: PhotoActivityType;
  challenge_id: string | null;
  speurtocht_id: string | null;
  agenda_event_id: string | null;
  
  // Location
  location_lat: number | null;
  location_lng: number | null;
  location_name: string | null;
  
  // User content
  description: string | null;
  tags: string[] | null;
  
  // Timestamps
  taken_at: string | null;
  created_at: string;
  updated_at: string;
  
  // Stats
  likes_count: number;
  comments_count: number;
  
  // Moderation
  is_visible: boolean;
  is_featured: boolean;
  
  // Joined data (optional)
  uploader?: Profile;
  is_liked?: boolean;
}

/**
 * Photo with full relations
 */
export interface PhotoWithRelations extends Photo {
  uploader: Profile;
  team?: {
    id: string;
    name: string;
    color: string | null;
  };
  weekend?: {
    id: string;
    name: string;
    year: number;
  };
  challenge?: {
    id: string;
    title: string;
  };
}

/**
 * Photo comment from database
 */
export interface PhotoComment {
  id: string;
  photo_id: string;
  user_id: string;
  content: string;
  is_edited: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  // Joined data
  user?: Profile;
}

/**
 * Photo like from database
 */
export interface PhotoLike {
  id: string;
  photo_id: string;
  user_id: string;
  created_at: string;
  // Joined data
  user?: Profile;
}

/**
 * Photo filter options
 */
export interface PhotoFilters {
  weekendId?: string;
  userId?: string;
  teamId?: string;
  activityType?: PhotoActivityType;
  challengeId?: string;
  isFeatured?: boolean;
  searchQuery?: string;
}

/**
 * Photo upload data
 */
export interface PhotoUploadData {
  uri: string;
  fileName?: string;
  mimeType?: string;
  width?: number;
  height?: number;
  description?: string;
  tags?: string[];
  weekendId?: string;
  teamId?: string;
  activityType?: PhotoActivityType;
  challengeId?: string;
  speurtochtId?: string;
  agendaEventId?: string;
  location?: {
    latitude: number;
    longitude: number;
    name?: string;
  };
  takenAt?: string;
}

/**
 * Photo upload progress
 */
export interface PhotoUploadProgress {
  photoId: string;
  progress: number;
  status: "pending" | "uploading" | "processing" | "complete" | "error";
  error?: string;
}

/**
 * Photo gallery view mode
 */
export type PhotoViewMode = "grid" | "list" | "map";

/**
 * Photo sort options
 */
export type PhotoSortBy = "newest" | "oldest" | "most_liked" | "most_commented";

/**
 * Selected photos for multi-select operations
 */
export interface PhotoSelection {
  selectedIds: string[];
  isSelecting: boolean;
}

